import { Box, IconButton, useTheme } from "@mui/material";
import type { QueryKey } from "@tanstack/react-query";
import { useAtomValue, useSetAtom } from "jotai";
import { Plus } from "lucide-react";
import { type FC, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { DocumentKeys } from "web_myplann/Api/Document/DocumentKeys";
import { PortfolioKeys } from "web_myplann/Api/Portfolio/portfolioKeys";
import { usePortfolioRightOptional } from "web_myplann/Api/Rights/RightsProvider";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { AddDocumentDrawer } from "web_myplann/Portfolio/Components/Document/AddDocumentDrawer";
import { PortfolioHeaderActions } from "web_myplann/Portfolio/Components/PortfolioHeaderActions";
import {
	SortDrawer,
	type SortKey,
} from "web_myplann/Portfolio/Components/SortDrawer";
import {
	PORTFOLIO_TAB_STORAGE_KEY,
	PortfolioTab,
} from "web_myplann/Portfolio/portfolioTabs";
import { MyPlannDrawer } from "web_myplann/Shared/Components/MyPlannDrawer";
import { MyPlannLoading } from "web_myplann/Shared/Components/MyPlannLoading";
import { MyPlannTabs } from "web_myplann/Shared/Components/MyPlannTabs";
import { NativePullToRefresh } from "web_myplann/Shared/Components/NativePullToRefresh";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import NavigationHeader from "web_myplann/Shared/Header/NavigationHeader";
import { useResolveTabFromStorage } from "web_myplann/Shared/Hooks/useResolveTabFromStorage";
import { portfolioSortState } from "web_myplann/Shared/State/portfolioSortAtom";

export type PortfolioTabContext = {
	activeSort: SortKey;
	setIsLoading: (isFetching: boolean) => void;
};

const tabFromPath = (pathname: string): PortfolioTab =>
	pathname === Routing.Portfolio.DocumentsList.path()
		? PortfolioTab.Documents
		: PortfolioTab.Portfolio;

export const PortfolioTabsLayout: FC = () => {
	const theme = useTheme();
	const { t } = useResource();
	const navigate = useNavigate();
	const { pathname } = useLocation();

	const isResolving = useResolveTabFromStorage({
		parentPath: Routing.Portfolio.PortfolioTabs.path(),
		storageKey: PORTFOLIO_TAB_STORAGE_KEY,
		tabs: [
			{
				value: PortfolioTab.Portfolio,
				path: Routing.Portfolio.PortfolioList.path(),
			},
			{
				value: PortfolioTab.Documents,
				path: Routing.Portfolio.DocumentsList.path(),
			},
		],
	});

	const activeTab = tabFromPath(pathname);

	const [sorterDrawer, setSorterDrawer] = useState(false);
	const [documentDrawer, setDocumentDrawer] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	const hasPortfolioPilotRight = usePortfolioRightOptional();

	const { portfolioSortValue } = useAtomValue(portfolioSortState);
	const setPortfolioSortState = useSetAtom(portfolioSortState);
	const [activeSort, setActiveSort] = useState<SortKey>(portfolioSortValue);

	const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
		if (newValue === activeTab) return;
		localStorage.setItem(PORTFOLIO_TAB_STORAGE_KEY, String(newValue));
		navigate(
			newValue === PortfolioTab.Documents
				? Routing.Portfolio.DocumentsList.path()
				: Routing.Portfolio.PortfolioList.path(),
			{ replace: true },
		);
	};

	const isPortfolioTab = activeTab === PortfolioTab.Portfolio;

	const title = isPortfolioTab
		? t(Resources.Portfolio.Titles.portfolio)
		: t(Resources.Portfolio.Titles.document);

	const endComponent = isPortfolioTab ? (
		<PortfolioHeaderActions
			hasPortfolioPilotRight={hasPortfolioPilotRight}
			onSort={() => setSorterDrawer(true)}
			onAdd={() => navigate(Routing.Portfolio.PortfolioAddItem.path())}
		/>
	) : (
		<IconButton onClick={() => setDocumentDrawer(true)}>
			<Plus color={theme.palette.primary.main} />
		</IconButton>
	);

	const queryKeys: QueryKey[] = isPortfolioTab
		? [
				PortfolioKeys.portfolioList(),
				PortfolioKeys.propertyList(),
				PortfolioKeys.reportingSummary(),
			]
		: [DocumentKeys.allLists];

	const outletContext: PortfolioTabContext = { activeSort, setIsLoading };

	if (isResolving) {
		return (
			<MyPlannLayout
				disableContentPadding
				mainSx={{
					overflow: "visible",
					display: "flex",
					flexDirection: "column",
				}}
				topBar={
					<NavigationHeader
						title={t(Resources.Portfolio.Titles.portfolio)}
						disableBottomPadding
					/>
				}
			/>
		);
	}

	return (
		<MyPlannLayout
			disableContentPadding
			mainSx={{ overflow: "visible", display: "flex", flexDirection: "column" }}
			topBar={
				<NavigationHeader
					title={title}
					bottomComponent={
						<MyPlannTabs
							value={activeTab}
							onChange={handleTabChange}
							tabs={[
								{
									id: "portfolio",
									label: t(Resources.Portfolio.Titles.portfolio),
								},
								{
									id: "documents",
									label: t(Resources.Portfolio.Titles.document),
								},
							]}
						/>
					}
					endComponent={endComponent}
					disableBottomPadding
				/>
			}
		>
			{isLoading && <MyPlannLoading />}
			<Box
				key={pathname}
				sx={{
					flex: 1,
					minHeight: 0,
					overflowY: "auto",
				}}
			>
				<NativePullToRefresh queryKeys={queryKeys} spinnerOffset={-20}>
					<Outlet context={outletContext} />
				</NativePullToRefresh>
			</Box>
			<MyPlannDrawer
				open={sorterDrawer}
				onClose={() => setSorterDrawer(false)}
				title={t(Resources.Portfolio.List.Sorter.title)}
			>
				<SortDrawer
					activeSort={activeSort}
					setSort={(val) => {
						setActiveSort(val);
						setPortfolioSortState({ portfolioSortValue: val });
						setSorterDrawer(false);
					}}
				/>
			</MyPlannDrawer>
			<MyPlannDrawer
				open={documentDrawer}
				onClose={() => setDocumentDrawer(false)}
				title={t(Resources.Portfolio.List.AddDocumentDrawer.title)}
			>
				<AddDocumentDrawer onClose={() => setDocumentDrawer(false)} />
			</MyPlannDrawer>
		</MyPlannLayout>
	);
};
